import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

// ── Fixed absolute coordinates ──────────────────────────────────────────────
// Canvas: 1920 × 1080

// LEFT column — folder card center
const FOLDER_CY = 270; // card top=200, height=140 → center=270
const FOLDER_RIGHT = 260; // right edge of folder card (100+160)

// CENTER column — badges area
const BADGES_CX = 730;
const BADGES_TOP = 170;
const BADGES_BOTTOM = 290; // approx bottom of badge rows

// CENTER — metadata box
const META_LEFT = 600;
const META_TOP = 400;
const META_HEIGHT = 80;
const META_CY = META_TOP + META_HEIGHT / 2; // 440
const META_RIGHT = 860;
const META_CX = 730;
const META_BOTTOM = META_TOP + META_HEIGHT; // 480

// RIGHT column — result cards
const RESULTS_LEFT = 1300;
const RESULTS_TOP = 200; // first card top
const RESULTS_CX = 1300 + 180; // center of 360-wide card

// BOTTOM — source dashboard
const DASH_CX = 730; // horizontally centered between meta-box and viewport
const DASH_TOP = 750;
const DASH_LEFT = 660; // 660 + 600/2 = 960 center
const DASH_RIGHT = 1260;

// ── Data ────────────────────────────────────────────────────────────────────

interface DocBadge {
  label: string;
  color: string;
  delay: number;
}

const DOC_BADGES: DocBadge[] = [
  { label: "12 emails", color: COLORS.neonPink, delay: 50 },
  { label: "3 proposals", color: COLORS.neonBlue, delay: 65 },
  { label: "1 pricing sheet", color: COLORS.neonGreen, delay: 80 },
  { label: "2 meeting notes", color: COLORS.neonOrange, delay: 95 },
  { label: "1 competitive analysis", color: COLORS.neonPurple, delay: 110 },
];

interface MetaResult {
  label: string;
  value: string;
  color: string;
  delay: number;
}

const META_RESULTS: MetaResult[] = [
  { label: "Companies", value: "Reliance, TCS, Bajaj", color: COLORS.neonBlue, delay: 140 },
  { label: "People", value: "8 contacts", color: COLORS.neonGreen, delay: 160 },
  { label: "Amounts", value: "₹4.5L, $500/mo", color: COLORS.neonYellow, delay: 180 },
  { label: "Commitments", value: "3 pending", color: COLORS.neonOrange, delay: 200 },
  { label: "Sentiment", value: "6+ 4= 2-", color: COLORS.neonPink, delay: 220 },
];

// ── Animated data particles ──────────────────────────────────────────────────

interface ParticleDef {
  color: string;
  speed: number;
  offset: number;
  // path: 0 = folder→badges, 1 = badges→meta, 2 = meta→results, 3 = meta→dash, 4 = results→dash
  path: number;
}

const PARTICLES: ParticleDef[] = [
  { color: COLORS.neonPink, speed: 0.004, offset: 0, path: 0 },
  { color: COLORS.neonBlue, speed: 0.0035, offset: 0.3, path: 0 },
  { color: COLORS.neonCyan, speed: 0.005, offset: 0.15, path: 1 },
  { color: COLORS.neonCyan, speed: 0.004, offset: 0.6, path: 1 },
  { color: COLORS.neonGreen, speed: 0.0045, offset: 0, path: 2 },
  { color: COLORS.neonGreen, speed: 0.004, offset: 0.5, path: 2 },
  { color: COLORS.neonGreen, speed: 0.003, offset: 0.2, path: 3 },
  { color: COLORS.neonGreen, speed: 0.0035, offset: 0.7, path: 4 },
];

function lerpPoint(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  t: number
): [number, number] {
  return [ax + (bx - ax) * t, ay + (by - ay) * t];
}

function getParticlePos(path: number, t: number): [number, number] {
  const tc = Math.max(0, Math.min(1, t));
  switch (path) {
    case 0: // folder right edge → first badge left
      return lerpPoint(FOLDER_RIGHT, FOLDER_CY, BADGES_CX - 150, BADGES_TOP + 15, tc);
    case 1: // badges center bottom → metadata box top
      return lerpPoint(BADGES_CX, BADGES_BOTTOM, META_CX, META_TOP, tc);
    case 2: // metadata box right → results left
      return lerpPoint(META_RIGHT, META_CY, RESULTS_LEFT, RESULTS_TOP + 25, tc);
    case 3: // metadata box bottom → dashboard top
      return lerpPoint(META_CX, META_BOTTOM, DASH_CX, DASH_TOP, tc);
    case 4: // results → dashboard
      return lerpPoint(RESULTS_CX, RESULTS_TOP + 150, DASH_RIGHT - 50, DASH_TOP, tc);
    default:
      return [0, 0];
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export const Scene07Phase2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  // Exit fade
  const exitOpacity = interpolate(
    frame,
    [durationInFrames - 30, durationInFrames],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // Title
  const titleSpring = spring({ frame, fps, config: SPRING.snappy });
  const titleY = interpolate(titleSpring, [0, 1], [-24, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const titleOpacity = interpolate(titleSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Folder
  const folderSpring = spring({ frame: frame - 30, fps, config: SPRING.bouncy });
  const folderOpacity = interpolate(folderSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const folderScale = interpolate(folderSpring, [0, 1], [0.7, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Arrow 1: folder → badges (dash offset animates from frame 40)
  const arrow1Progress = interpolate(frame, [40, 90], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // Arrow 2: badges → meta
  const arrow2Progress = interpolate(frame, [100, 130], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // Arrow 3: meta → results
  const arrow3Progress = interpolate(frame, [130, 165], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // Arrow 4a: meta → dashboard
  const arrow4aProgress = interpolate(frame, [240, 285], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  // Arrow 4b: results → dashboard
  const arrow4bProgress = interpolate(frame, [255, 295], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Metadata box
  const metaSpring = spring({ frame: frame - 110, fps, config: SPRING.snappy });
  const metaOpacity = interpolate(metaSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const metaScale = interpolate(metaSpring, [0, 1], [0.85, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Source dashboard
  const dashSpring = spring({ frame: frame - 280, fps, config: SPRING.punch });
  const dashOpacity = interpolate(dashSpring, [0, 1], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  const dashScale = interpolate(dashSpring, [0, 1], [0.7, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Breathing / parallax
  const breathe = Math.sin(frame * 0.012) * 4;
  const gridShift = Math.sin(frame * 0.008) * 8;
  const orb1X = Math.sin(frame * 0.007) * 60;
  const orb1Y = Math.cos(frame * 0.009) * 40;
  const orb2X = Math.cos(frame * 0.006) * 80;
  const orb2Y = Math.sin(frame * 0.008) * 50;
  const orb3X = Math.sin(frame * 0.005 + 1) * 50;
  const orb3Y = Math.cos(frame * 0.007 + 2) * 35;

  // ── Arrow path helpers ─────────────────────────────────────────────────────
  // Arrow 1: folder right (260, 270) → first badge left approx (580, 185)
  const A1x1 = 260, A1y1 = 270, A1x2 = 580, A1y2 = 185;
  // Arrow 2: badges center bottom (730, 290) → meta box top center (730, 400)
  const A2x1 = 730, A2y1 = 290, A2x2 = 730, A2y2 = 400;

  // Arrow 3: meta box right (860, 440) → results top-left (1300, 200)
  const A3x1 = 860, A3y1 = 440, A3x2 = 1300, A3y2 = 200;

  // Arrow 4a: meta box bottom (730, 480) → dashboard top (730, 750)
  const A4ax1 = 730, A4ay1 = 480, A4ax2 = 730, A4ay2 = 750;

  // Arrow 4b: results column center (1480, 470) → dashboard right (1260, 750)
  const A4bx1 = 1480, A4by1 = 470, A4bx2 = 1260, A4by2 = 750;

  // Animated dashes (frame-driven marching ants)
  const dashAnim = frame * 2;

  return (
    <AbsoluteFill
      style={{
        background: COLORS.bg,
        opacity: exitOpacity,
        overflow: "hidden",
      }}
    >
      {/* ── Parallax background ─────────────────────────────────────────── */}

      {/* Grid */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          opacity: 0.04,
          transform: `translateY(${gridShift}px)`,
          pointerEvents: "none",
        }}
      >
        <defs>
          <pattern id="grid07" width="60" height="60" patternUnits="userSpaceOnUse">
            <path
              d="M 60 0 L 0 0 0 60"
              fill="none"
              stroke={COLORS.neonBlue}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid07)" />
      </svg>

      {/* Orb 1 — blue */}
      <div
        style={{
          position: "absolute",
          left: 500 + orb1X,
          top: 200 + orb1Y,
          width: 700,
          height: 500,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${COLORS.neonBlue}10 0%, transparent 70%)`,
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      {/* Orb 2 — purple */}
      <div
        style={{
          position: "absolute",
          left: 1100 + orb2X,
          top: 300 + orb2Y,
          width: 600,
          height: 400,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${COLORS.neonPurple}08 0%, transparent 70%)`,
          filter: "blur(80px)",
          pointerEvents: "none",
        }}
      />
      {/* Orb 3 — green */}
      <div
        style={{
          position: "absolute",
          left: 200 + orb3X,
          top: 600 + orb3Y,
          width: 500,
          height: 350,
          borderRadius: "50%",
          background: `radial-gradient(ellipse, ${COLORS.neonGreen}08 0%, transparent 70%)`,
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      {/* Small floating particles */}
      {[
        { x: 300, y: 150, color: COLORS.neonBlue, speed: 0.015, size: 3 },
        { x: 900, y: 90, color: COLORS.neonPink, speed: 0.011, size: 2 },
        { x: 1500, y: 200, color: COLORS.neonGreen, speed: 0.018, size: 3 },
        { x: 150, y: 700, color: COLORS.neonCyan, speed: 0.013, size: 2 },
        { x: 1700, y: 600, color: COLORS.neonPurple, speed: 0.016, size: 3 },
        { x: 800, y: 950, color: COLORS.neonYellow, speed: 0.012, size: 2 },
      ].map((p, i) => (
        <div
          key={`fp-${i}`}
          style={{
            position: "absolute",
            left: p.x + Math.sin(frame * p.speed + i) * 20,
            top: p.y + Math.cos(frame * p.speed * 0.7 + i) * 15,
            width: p.size,
            height: p.size,
            borderRadius: "50%",
            background: p.color,
            boxShadow: `0 0 6px ${p.color}`,
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* ── SVG layer: all arrows ───────────────────────────────────────── */}
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 1920,
          height: 1080,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        <defs>
          {/* Arrowhead markers */}
          <marker
            id="arr-neonYellow"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonYellow} />
          </marker>
          <marker
            id="arr-neonCyan"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonCyan} />
          </marker>
          <marker
            id="arr-neonGreen-a3"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonGreen} />
          </marker>
          <marker
            id="arr-neonGreen-a4a"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonGreen} />
          </marker>
          <marker
            id="arr-neonGreen-a4b"
            markerWidth="8"
            markerHeight="8"
            refX="6"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L0,6 L8,3 z" fill={COLORS.neonGreen} />
          </marker>
        </defs>

        {/* Arrow 1: folder → badges (neonYellow dashed) */}
        <line
          x1={A1x1}
          y1={A1y1}
          x2={A1x2}
          y2={A1y2}
          stroke={COLORS.neonYellow}
          strokeWidth="2"
          strokeDasharray="8 5"
          strokeDashoffset={-dashAnim}
          opacity={arrow1Progress}
          markerEnd="url(#arr-neonYellow)"
          style={{ filter: `drop-shadow(0 0 4px ${COLORS.neonYellow}80)` }}
        />

        {/* Arrow 2: badges → metadata box (neonCyan dashed) */}
        <line
          x1={A2x1}
          y1={A2y1}
          x2={A2x2}
          y2={A2y2}
          stroke={COLORS.neonCyan}
          strokeWidth="2"
          strokeDasharray="8 5"
          strokeDashoffset={-dashAnim}
          opacity={arrow2Progress}
          markerEnd="url(#arr-neonCyan)"
          style={{ filter: `drop-shadow(0 0 4px ${COLORS.neonCyan}80)` }}
        />

        {/* Arrow 3: metadata box → results (neonGreen dashed) */}
        <line
          x1={A3x1}
          y1={A3y1}
          x2={A3x2}
          y2={A3y2}
          stroke={COLORS.neonGreen}
          strokeWidth="2"
          strokeDasharray="8 5"
          strokeDashoffset={-dashAnim}
          opacity={arrow3Progress}
          markerEnd="url(#arr-neonGreen-a3)"
          style={{ filter: `drop-shadow(0 0 4px ${COLORS.neonGreen}80)` }}
        />

        {/* Arrow 4a: metadata box → dashboard (neonGreen solid) */}
        <line
          x1={A4ax1}
          y1={A4ay1}
          x2={A4ax2}
          y2={A4ay2}
          stroke={COLORS.neonGreen}
          strokeWidth="2.5"
          strokeDasharray="10 5"
          strokeDashoffset={-dashAnim}
          opacity={arrow4aProgress}
          markerEnd="url(#arr-neonGreen-a4a)"
          style={{ filter: `drop-shadow(0 0 6px ${COLORS.neonGreen}80)` }}
        />

        {/* Arrow 4b: results → dashboard (neonGreen dashed) */}
        <line
          x1={A4bx1}
          y1={A4by1}
          x2={A4bx2}
          y2={A4by2}
          stroke={COLORS.neonGreen}
          strokeWidth="2"
          strokeDasharray="8 5"
          strokeDashoffset={-dashAnim}
          opacity={arrow4bProgress}
          markerEnd="url(#arr-neonGreen-a4b)"
          style={{ filter: `drop-shadow(0 0 4px ${COLORS.neonGreen}80)` }}
        />

        {/* ── Animated data particles ───────────────────────────────────── */}
        {PARTICLES.map((p, i) => {
          const t = ((frame * p.speed + p.offset) % 1 + 1) % 1;
          const [px, py] = getParticlePos(p.path, t);
          return (
            <circle
              key={`par-${i}`}
              cx={px}
              cy={py}
              r={3}
              fill={p.color}
              opacity={0.9}
              style={{
                filter: `drop-shadow(0 0 5px ${p.color}) drop-shadow(0 0 10px ${p.color}60)`,
              }}
            />
          );
        })}
      </svg>

      {/* ── Title ──────────────────────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: 28,
          left: "50%",
          transform: `translateX(-50%) translateY(${titleY}px)`,
          opacity: titleOpacity,
          textAlign: "center",
          whiteSpace: "nowrap",
        }}
      >
        <div
          style={{
            fontSize: 44,
            fontFamily: FONTS.heading,
            fontWeight: 800,
            color: COLORS.neonBlue,
            textShadow: `0 0 30px ${COLORS.neonBlue}80, 0 0 60px ${COLORS.neonBlue}30`,
          }}
        >
          Phase 2: Smart Ingestion
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            fontWeight: 600,
            color: COLORS.neonCyan,
            marginTop: 6,
            textShadow: `0 0 15px ${COLORS.neonCyan}60`,
          }}
        >
          Current Focus
        </div>
      </div>

      {/* ── 3D perspective wrapper ─────────────────────────────────────── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 1920,
          height: 1080,
          perspective: "1000px",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: 1920,
            height: 1080,
            transform: `rotateX(2deg) translateZ(${breathe}px)`,
            transformStyle: "preserve-3d",
          } as React.CSSProperties}
        >

          {/* ── LEFT: Folder card — absolute pos (100, 200) 160×140 ──── */}
          <div
            style={{
              position: "absolute",
              left: 100,
              top: 200,
              width: 160,
              height: 140,
              borderRadius: 20,
              background: `linear-gradient(135deg, ${COLORS.neonYellow}25, ${COLORS.neonOrange}15)`,
              border: `2px solid ${COLORS.neonYellow}60`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: `0 0 30px ${COLORS.neonYellow}40, 0 0 60px ${COLORS.neonYellow}15`,
              opacity: folderOpacity,
              transform: `scale(${folderScale})`,
            }}
          >
            <div style={{ fontSize: 52 }}>📁</div>
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 14,
                color: COLORS.neonYellow,
                textShadow: `0 0 10px ${COLORS.neonYellow}60`,
              }}
            >
              /Marketing/
            </div>
            <div
              style={{
                fontFamily: FONTS.body,
                fontSize: 11,
                color: COLORS.textMuted,
              }}
            >
              User uploads folder
            </div>
          </div>

          {/* ── CENTER TOP: Document type badges ── abs centered ~730 ── */}
          {/* Row 1 badges */}
          {DOC_BADGES.slice(0, 3).map((badge, i) => {
            const badgeSpring = spring({
              frame: frame - badge.delay,
              fps,
              config: SPRING.bouncy,
            });
            const bOpacity = interpolate(badgeSpring, [0, 1], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const bScale = interpolate(badgeSpring, [0, 1], [0.5, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const bY = interpolate(badgeSpring, [0, 1], [-16, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            // Row 1 badge widths approx: 100, 110, 130 → spread around cx=730
            const row1Widths = [100, 110, 130];
            const row1Gap = 10;
            const row1Total =
              row1Widths.reduce((a, b) => a + b, 0) + row1Gap * (row1Widths.length - 1);
            const row1StartX = 730 - row1Total / 2;
            const leftOffset =
              row1Widths.slice(0, i).reduce((a, b) => a + b, 0) + row1Gap * i;

            return (
              <div
                key={`badge-r1-${i}`}
                style={{
                  position: "absolute",
                  left: row1StartX + leftOffset,
                  top: 170,
                  width: row1Widths[i],
                  height: 34,
                  borderRadius: 17,
                  background: `${badge.color}20`,
                  border: `1.5px solid ${badge.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: badge.color,
                  boxShadow: `0 0 12px ${badge.color}40`,
                  whiteSpace: "nowrap",
                  opacity: bOpacity,
                  transform: `scale(${bScale}) translateY(${bY}px)`,
                }}
              >
                {badge.label}
              </div>
            );
          })}

          {/* Row 2 badges */}
          {DOC_BADGES.slice(3).map((badge, i) => {
            const badgeSpring = spring({
              frame: frame - badge.delay,
              fps,
              config: SPRING.bouncy,
            });
            const bOpacity = interpolate(badgeSpring, [0, 1], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const bScale = interpolate(badgeSpring, [0, 1], [0.5, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const bY = interpolate(badgeSpring, [0, 1], [-16, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const row2Widths = [130, 180];
            const row2Gap = 10;
            const row2Total =
              row2Widths.reduce((a, b) => a + b, 0) + row2Gap * (row2Widths.length - 1);
            const row2StartX = 730 - row2Total / 2;
            const leftOffset =
              row2Widths.slice(0, i).reduce((a, b) => a + b, 0) + row2Gap * i;

            return (
              <div
                key={`badge-r2-${i}`}
                style={{
                  position: "absolute",
                  left: row2StartX + leftOffset,
                  top: 218,
                  width: row2Widths[i],
                  height: 34,
                  borderRadius: 17,
                  background: `${badge.color}20`,
                  border: `1.5px solid ${badge.color}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: FONTS.mono,
                  fontSize: 12,
                  color: badge.color,
                  boxShadow: `0 0 12px ${badge.color}40`,
                  whiteSpace: "nowrap",
                  opacity: bOpacity,
                  transform: `scale(${bScale}) translateY(${bY}px)`,
                }}
              >
                {badge.label}
              </div>
            );
          })}

          {/* ── CENTER BOTTOM: Metadata Extraction box — abs (600, 400) 260×80 */}
          <div
            style={{
              position: "absolute",
              left: META_LEFT,
              top: META_TOP,
              width: 260,
              height: META_HEIGHT,
              borderRadius: 16,
              background: `linear-gradient(135deg, ${COLORS.neonYellow}18, ${COLORS.neonYellow}08)`,
              border: `2px solid ${COLORS.neonYellow}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              boxShadow: `0 0 25px ${COLORS.neonYellow}50, 0 0 50px ${COLORS.neonYellow}20`,
              opacity: metaOpacity,
              transform: `scale(${metaScale}) perspective(800px) rotateX(5deg)`,
            } as React.CSSProperties}
          >
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: 17,
                fontWeight: 700,
                color: COLORS.neonYellow,
                textShadow: `0 0 12px ${COLORS.neonYellow}60`,
              }}
            >
              🔬 Metadata Extraction
            </div>
            <div
              style={{
                fontSize: 11,
                fontFamily: FONTS.body,
                color: COLORS.textMuted,
                fontWeight: 400,
              }}
            >
              NLP · Entity Recognition · Sentiment
            </div>
          </div>

          {/* ── RIGHT: Extracted metadata result cards ─────────────────── */}
          {META_RESULTS.map((result, i) => {
            const resultSpring = spring({
              frame: frame - result.delay,
              fps,
              config: SPRING.snappy,
            });
            const rOpacity = interpolate(resultSpring, [0, 1], [0, 1], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const rX = interpolate(resultSpring, [0, 1], [60, 0], {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
            });
            const isSentiment = result.label === "Sentiment";

            return (
              <div
                key={`result-${i}`}
                style={{
                  position: "absolute",
                  left: RESULTS_LEFT,
                  top: RESULTS_TOP + i * 62,
                  width: 360,
                  height: 50,
                  borderRadius: 12,
                  background: `${result.color}12`,
                  border: `1px solid ${result.color}40`,
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "0 18px",
                  boxShadow: `0 0 14px ${result.color}20`,
                  opacity: rOpacity,
                  transform: `translateX(${rX}px)`,
                }}
              >
                <span
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    color: COLORS.textMuted,
                    minWidth: 88,
                    flexShrink: 0,
                  }}
                >
                  {result.label}:
                </span>
                {isSentiment ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: COLORS.neonGreen,
                        fontWeight: 700,
                      }}
                    >
                      6+
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: COLORS.neonYellow,
                        fontWeight: 700,
                      }}
                    >
                      4=
                    </span>
                    <span
                      style={{
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: COLORS.neonRed,
                        fontWeight: 700,
                      }}
                    >
                      2-
                    </span>
                  </div>
                ) : (
                  <span
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 13,
                      color: result.color,
                      fontWeight: 600,
                      textShadow: `0 0 8px ${result.color}60`,
                    }}
                  >
                    {result.value}
                  </span>
                )}
              </div>
            );
          })}

          {/* ── BOTTOM: Source Dashboard — abs (660, 750) 600×70 ──────── */}
          <div
            style={{
              position: "absolute",
              left: DASH_LEFT,
              top: DASH_TOP,
              width: 600,
              height: 70,
              borderRadius: 16,
              background: `linear-gradient(135deg, ${COLORS.neonGreen}18, ${COLORS.neonGreen}08)`,
              border: `2px solid ${COLORS.neonGreen}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              boxShadow: `0 0 35px ${COLORS.neonGreen}60, 0 0 70px ${COLORS.neonGreen}25`,
              opacity: dashOpacity,
              transform: `scale(${dashScale}) perspective(600px) rotateX(-3deg)`,
            } as React.CSSProperties}
          >
            <div
              style={{
                fontFamily: FONTS.heading,
                fontSize: 20,
                fontWeight: 700,
                color: COLORS.neonGreen,
                textShadow: `0 0 15px ${COLORS.neonGreen}70`,
              }}
            >
              📊 Source Dashboard
            </div>
            <div
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: COLORS.textMuted,
                fontWeight: 400,
              }}
            >
              Unified view of all ingested knowledge
            </div>
          </div>

        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
